import { BaseConfig, useConfig as hook } from '@eika-infrastruktur/config-provider';

import { paths } from '@components/app/routes';
import { Translations, translations } from '@domain/translations';
import { KortbetjeningText } from '@hooks/use-text';

import sitecoreSimulationData from './config/dictionary/sitecore-simulation-data';

export const REPO_NAMESPACE = 'kredittkort.betjening';

export type Link = { url: string };

export type SitecoreLinks = {
    linkCardDetails: Link;
    stepUpAuthLink: Link;
    linkContactInfoEdit: Link;

    redirectURL: Link;
    linkCardControl: Link;
    linkComplaint: Link;
    linkTransactions: Link;
    linkInsuranceProof: Link;
    linkVisaExchangeCalculator: Link;
    linkBlockSelectedCard: Link;
    linkCloseCreditAccountTransfer: Link;
    linkHardBlockCardSelectReason: Link;
    linkDocuments: Link;
    linkCardRules: Link;
    linkHardBlockCardConfirm: Link;
    linkCloseCreditAccountSelectAccount: Link;
    linkSoftBlockCardConfirm: Link;
    linkSelectOrderNewCard: Link;
    linkOrderCardSelectAccount: Link;
    linkOrderCardResult: Link;
    linkReplaceCardPinChangeDecision: Link;
    linkReplaceCardNewPinEntry: Link;
    linkCreditLimits: Link;
    linkContactInfo: Link;
    linkCloseCreditAccountConfirm: Link;
    linkOrderCardPinDecision: Link;
    linkOrderCardSelectPin: Link;
    linkOrderCardConfirm: Link;
    linkOrderCardSummary: Link;
    cardLandingPage: Link;
    linkClick2Pay: Link;
    // v1 links
    linkCardOverview: Link;
    linkCreditCardApplication: Link;
    linkComingSoon: Link;
    linkCreditLimit: Link;
    linkGoogleWallet: Link;
    linkGooglePay: Link;
    linkOrderCardSigningPriorSummary: Link;
    linkOrderCardSigningResult: Link;
    linkPayment: Link;
    linkRegionBlock: Link;
    linkReplaceCardConfirm: Link;
    linkReplaceCardSelectCard: Link;
    linkReplaceCardSelectReason: Link;
    linkReplaceCardCancel: Link;
    linkSpendingLimits: Link;
    linkThirdPartyDependencies: Link;
    linkTransfer: Link;
    linkLogout: Link;
    linkHardBlockCardPinChangeDecision: Link;
    linkHardBlockCardNewPinEntry: Link;
    linkExtraPayment: Link;

    creditCardApplicationUrl: Link;

    // kredittbanken
    kredittbankenUsagePermissions: Link;
    kredittbankenChangeCreditLimit: Link;
    kredittbankenBlockReplace: Link;

    kredittbankenBlockCard: Link;
    kredittbankenUnblockCard: Link;
    kredittbankenOrderCard: Link;

    kredittbankenDocuments: Link;
    kredittbankenInsuranceProof: Link;
    kredittbankenAdditionalCard: Link;
    kredittbankenTransfer: Link;
    kredittbankenChangeDueDate: Link;
};

export type SitecoreLink = keyof SitecoreLinks;

export interface Config extends BaseConfig {
    links: SitecoreLinks;
    text: KortbetjeningText;
    translations: Translations;
    setup: {
        newPinBanks: string;
        useRouter?: string;
    };
    minAppVersionDebitCardEnrollment?: string;
}

export const DEFAULT_CONFIG: Config = {
    // used for testing
    links: {
        // not used
        linkCardDetails: { url: paths.CARD_PAGE.path },
        // not used
        stepUpAuthLink: { url: '/' },
        // not used
        linkContactInfoEdit: { url: paths.CARD_UPDATE_CONTACT_INFO_PAGE.path },

        // not used
        redirectURL: { url: 'https://www.eika.no' },
        // not used
        linkCardControl: { url: paths.SPENDING_LIMITS_PAGE.path },
        // not used
        linkComplaint: { url: paths.COMPLAINT_PAGE.path },
        // not used
        linkTransactions: { url: paths.CARD_TRANSACTION_PAGE.path },
        // not used
        linkInsuranceProof: { url: paths.INSURANCE_PROOF_PAGE.path },
        // not used
        linkVisaExchangeCalculator: { url: paths.CURRENCY_CALCULATOR_PAGE.path },
        // not used
        linkBlockSelectedCard: { url: paths.SELECT_BLOCK_TYPE_PAGE.path },
        // not used
        linkCloseCreditAccountTransfer: { url: paths.CLOSE_CREDIT_ACCOUNT_TRANSFER_FORM_PAGE.path },
        // not used
        linkHardBlockCardSelectReason: { url: paths.SELECT_REASON_PAGE.path },
        // not used
        linkDocuments: { url: paths.CARD_DOCUMENTS_PAGE.path },
        // not used
        linkCardRules: { url: paths.CARD_RULES_PAGE.path },
        // not used
        linkHardBlockCardConfirm: { url: paths.HARD_BLOCK_CARD_CONFIRM_PAGE.path },
        // not used
        linkCloseCreditAccountSelectAccount: { url: 'oppsigelse' },
        // not used
        linkSoftBlockCardConfirm: { url: paths.SOFT_BLOCK_CARD_CONFIRM_PAGE.path },
        // not used
        linkSelectOrderNewCard: { url: paths.SELECT_REORDER_CARD_PAGE.path },
        // not used
        linkOrderCardSelectAccount: { url: paths.ORDER_CARD_PAGE.path },
        // not used
        linkOrderCardResult: { url: paths.ORDER_CARD_PAGE_RESULT.path },
        // not used
        linkReplaceCardPinChangeDecision: { url: paths.PIN_CHANGE_DECISION_PAGE.path },
        // not used
        linkReplaceCardNewPinEntry: { url: paths.NEW_PIN_ENTRY_PAGE.path },
        // not used
        linkCreditLimits: { url: paths.CARD_CREDIT_LIMIT_PAGE.path },
        // not used
        linkContactInfo: { url: paths.CARD_CONTACT_INFO_PAGE.path },
        // not used
        linkOrderCardPinDecision: { url: paths.ORDER_CARD_FORM_PIN_DECISION_PAGE.path },
        // not used
        linkOrderCardSelectPin: { url: paths.ORDER_CARD_FORM_SELECT_PIN_PAGE.path },
        // not used
        linkOrderCardConfirm: { url: paths.ORDER_CARD_FORM_SUMMARY_CONFIRM_PAGE.path },
        // not used
        linkOrderCardSummary: { url: paths.ORDER_CARD_FORM_SUMMARY_PAGE.path },
        // used
        cardLandingPage: { url: '/' },

        linkClick2Pay: { url: paths.CLICK_TO_PAY.path },
        linkCloseCreditAccountConfirm: { url: '/missing-url' },

        creditCardApplicationUrl: { url: '/nettbank/kort/sok-kredittkort' },

        // v1 links
        linkCardOverview: { url: '/missing-url' },
        linkCreditCardApplication: { url: '/missing-url' },
        linkComingSoon: { url: '/missing-url' },
        linkCreditLimit: { url: '/missing-url' },
        linkGoogleWallet: { url: '/missing-url' },
        linkGooglePay: { url: '/missing-url' },
        linkOrderCardSigningPriorSummary: { url: '/missing-url' },
        linkOrderCardSigningResult: { url: '/missing-url' },
        linkPayment: { url: '/missing-url' },
        linkRegionBlock: { url: '/missing-url' },
        linkReplaceCardConfirm: { url: '/missing-url' },
        linkReplaceCardSelectCard: { url: '/missing-url' },
        linkReplaceCardSelectReason: { url: '/missing-url' },
        linkReplaceCardCancel: { url: '/missing-url' },
        linkSpendingLimits: { url: '/missing-url' },
        linkThirdPartyDependencies: { url: '/missing-url' },
        linkTransfer: { url: '/missing-url' },
        linkLogout: { url: '/missing-url' },
        linkHardBlockCardPinChangeDecision: { url: '/missing-url' },
        linkHardBlockCardNewPinEntry: { url: '/missing-url' },
        linkExtraPayment: { url: '/missing-url' },

        // kredittbanken
        kredittbankenUsagePermissions: { url: '/TMP-LINK/kredittbankenUsagePermissions' },
        kredittbankenChangeCreditLimit: { url: '/TMP-LINK/kredittbankenChangeCreditLimit' },
        kredittbankenBlockReplace: { url: '/TMP-LINK/kredittbankenBlockReplace' },

        kredittbankenBlockCard: { url: '/TMP-LINK/kredittbankenBlockReplace' },
        kredittbankenUnblockCard: { url: '/TMP-LINK/kredittbankenBlockReplace' },
        kredittbankenOrderCard: { url: '/TMP-LINK/kredittbankenBlockReplace' },

        kredittbankenDocuments: { url: '/TMP-LINK/kredittbankenDocuments' },
        kredittbankenInsuranceProof: { url: '/TMP-LINK/kredittbankenInsuranceProof' },
        kredittbankenAdditionalCard: { url: '/TMP-LINK/kredittbankenAdditionalCard' },

        kredittbankenTransfer: { url: '/TMP-LINK/kredittbankenTransfer' },
        kredittbankenChangeDueDate: { url: '/TMP-LINK/kredittbankenChangeDueDate' },
    },
    text: sitecoreSimulationData.text,
    translations,
    setup: {
        newPinBanks: '1830,3240',
        useRouter: 'true',
    },
    minAppVersionDebitCardEnrollment: '17.0.0',
};

function createHook<T>(): () => T {
    return () => hook<T>();
}

export const useConfig = createHook<typeof DEFAULT_CONFIG>();
